'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useTrackEvent } from '@/lib/analytics/hooks/useTrackEvent';
import type { SupportedLocale } from '@/lib/translation/types';
import { createBookingSession, updateBookingSession } from '@/lib/booking-api/session';
import { getConsentData } from '@/components/cookie-modal';
type CTAMode = 'scroll' | 'route';
type CTAType = 'lead_collection' | 'booking';
interface CTAProps {
  type: CTAType;
  mode?: CTAMode;
  treatmentName: string;
  treatmentSlug?: string;
  treatmentId?: number | string;
  className?: string;
}
const truncateToTwoWords = (text: string) => {
  const words = text.split(' ');
  return words.length > 2 ? `${words.slice(0, 2).join(' ')}...` : text;
};
export function ScrollButton({
  type,
  mode = 'scroll',
  treatmentName,
  treatmentSlug,
  treatmentId,
  className
}: CTAProps) {
  const t = useTranslations('cta');
  const router = useRouter();
  const locale = useLocale() as SupportedLocale;
  const {
    track
  } = useTrackEvent();
  const buttonText = type === 'booking' ? t('book-now') : t('free-consultation');
  const handleClick = async () => {
    if (type === 'lead_collection') {
      try {
        // Track consultation initiation with the new analytics architecture
        track({
          name: 'BookConsultation',
          properties: {
            content_name: treatmentName,
            content_type: 'treatment',
            content_id: treatmentSlug,
            event_source: 'cta-button',
            booking_step: 'initiation'
          },
          options: {
            isHealthRelated: true,
            eventId: `consultation_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
          }
        });
        router.push(`/treatment/${treatmentSlug}/consultation`);
      } catch (error) {
        console.error('Failed to track consultation event:', error);
        // Continue execution even if tracking fails
        router.push(`/treatment/${treatmentSlug}/consultation`);
      }
      return;
    }
    if (!treatmentSlug) {
      console.error('No treatment slug provided for booking');
      return;
    }
    if (!treatmentId) {
      console.error('No treatment ID provided for booking');
      return;
    }
    try {
      // Track booking initiation with the new analytics architecture
      track({
        name: 'Book',
        properties: {
          content_type: 'treatment',
          content_id: treatmentId,
          content_name: treatmentName,
          event_source: 'cta-button',
          booking_step: 'initiation'
        },
        options: {
          isHealthRelated: true,
          eventId: `booking_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
        }
      });
      const session = await createBookingSession(locale);
      if (!session) {
        throw new Error('Failed to create session');
      }
      await updateBookingSession(session.id, {
        treatmentId: Number(treatmentId),
        step: 'treatment'
      });
      router.push(`/book/treatment?sid=${session.id}`);
    } catch (error) {
      console.error('Failed to initiate booking:', error);
    }
  };
  return <Button className={cn('bg-offblack hover:bg-offblue py-7 md:py-3 px-4 md:px-6 inline-flex items-center', className)} onClick={handleClick} data-sentry-element="Button" data-sentry-component="ScrollButton" data-sentry-source-file="cta-elements.tsx">
      <span className="flex-1 min-w-0 flex items-center justify-center ">
        <span>{buttonText}</span>
      </span>
    </Button>;
}
export function CTABanner({
  type,
  mode = 'scroll',
  treatmentName,
  treatmentSlug,
  treatmentId,
  className
}: CTAProps) {
  const t = useTranslations('cta');
  const title = type === 'booking' ? t('plan-treatment', {
    treatment: treatmentName
  }) : t('plan-treatment', {
    treatment: treatmentName
  });
  const subtitle = type === 'booking' ? t('select-time') : t('schedule-consultation');
  return <div className={cn('flex flex-col md:flex-row justify-between items-center p-4 md:p-8 bg-white rounded-xl shadow-xl max-w-5xl mx-auto', className)} data-sentry-component="CTABanner" data-sentry-source-file="cta-elements.tsx">
      <div className="text-center md:text-left mb-4 md:mb-0">
        <h2 className="text-2xl md:text-4xl font-serif">{title}</h2>
        {subtitle && <p className="text-gray-500 mt-2">{subtitle}</p>}
      </div>
      <ScrollButton type={type} mode={mode} treatmentName={treatmentName} treatmentSlug={treatmentSlug} treatmentId={treatmentId} className="w-full md:w-auto text-center " data-sentry-element="ScrollButton" data-sentry-source-file="cta-elements.tsx" />
    </div>;
}